import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bannerAPI, newsAPI, videoAPI, adsAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [counts, setCounts] = useState({
        banners: 0,
        news: 0,
        videos: 0,
        ads: 0,
    });
    const [todayCounts, setTodayCounts] = useState({
        news: 0,
        videos: 0,
        ads: 0,
    });

    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const [bannersRes, newsRes, videosRes, adsRes] = await Promise.all([
                    bannerAPI.getAll(),
                    newsAPI.getAll(),
                    videoAPI.getAll(),
                    adsAPI.getAll(),
                ]);

                const banners = bannersRes.data.data || [];
                const news = newsRes.data.data || [];
                const videos = videosRes.data.data || [];
                const ads = adsRes.data.data || [];

                const today = new Date().toISOString().split('T')[0];

                const todayNews = news.filter(item => item.created_at?.split('T')[0] === today);
                const todayVideos = videos.filter(item => item.created_at?.split('T')[0] === today);
                const todayAds = ads.filter(item => item.created_at?.split('T')[0] === today);

                setCounts({
                    banners: banners.length,
                    news: news.length,
                    videos: videos.length,
                    ads: ads.length,
                });

                setTodayCounts({
                    news: todayNews.length,
                    videos: todayVideos.length,
                    ads: todayAds.length,
                });

                setLoading(false);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data.');
                toast.error('Could not load counts. Please try again.');
                setLoading(false);
            }
        };

        fetchCounts();
    }, []);

    // Style for highlighted title label
    const titleStyle = {

        color: '#ffffff',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.5px',
        display: 'inline-block',
    };

    // Style for the card itself
    const cardStyle = {
        backgroundColor: 'maroon', // light blue
        color: '#ffffff',
        border: 'none',
    };

    // Style for links inside the card (white text)
    const linkStyle = {
        color: '#ffffff',
        textDecoration: 'none',
        fontWeight: 500,
    };

    if (loading) {
        return (
            <div className="main-content">
                <div className="container-fluid p-3 p-md-4">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading dashboard data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="container-fluid p-3 p-md-4">
                {/* Page Header */}
                <div className="dashboard-header mb-4">
                    <h1 className="dashboard-title">
                        <i className="fas fa-tachometer-alt me-2"></i> Dashboard
                    </h1>
                    <p className="dashboard-subtitle">Welcome back! Here's what's happening with your CMS today.</p>
                </div>

                {/* Row 1: Total Counts */}
                <div className="row g-3 mb-4">
                    <div className="col-sm-6 col-xl-4">
                        <div className="stat-card" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        TOTAL BANNERS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-cubes text-primary"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{counts.banners}</div>
                                <Link to="/admin/banners" style={linkStyle}>View all →</Link>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-xl-4">
                        <div className="stat-card" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        TOTAL NEWS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-newspaper text-info"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{counts.news}</div>
                                <Link to="/admin/news-list" style={linkStyle}>View all →</Link>
                            </div>
                        </div>
                    </div>

                    {/* <div className="col-sm-6 col-xl-3">
                        <div className="stat-card" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        TOTAL VIDEOS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-video text-success"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{counts.videos}</div>
                                <Link to="/admin/videos-list" style={linkStyle}>View all →</Link>
                            </div>
                        </div>
                    </div> */}

                    <div className="col-sm-6 col-xl-4">
                        <div className="stat-card" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        TOTAL ADS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-ad text-warning"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{counts.ads}</div>
                                <Link to="/admin/ads-list" style={linkStyle}>View all →</Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: Today's Counts */}
                <div className="row g-3 mb-4">
                    <div className="col-sm-6 col-xl-6">
                        <div className="stat-card stat-card-today" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        <i className="fas fa-calendar-day me-1"></i> TODAY'S NEWS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-newspaper text-info"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{todayCounts.news}</div>
                                <Link to="/admin/news-list" style={linkStyle}>View today →</Link>
                            </div>
                        </div>
                    </div>

                    {/* <div className="col-sm-6 col-xl-4">
                        <div className="stat-card stat-card-today" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        <i className="fas fa-calendar-day me-1"></i> TODAY'S VIDEOS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-video text-success"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{todayCounts.videos}</div>
                                <Link to="/admin/videos-list" style={linkStyle}>View today →</Link>
                            </div>
                        </div>
                    </div> */}

                    <div className="col-sm-6 col-xl-6">
                        <div className="stat-card stat-card-today" style={cardStyle}>
                            <div className="stat-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <span className="stat-label" style={titleStyle}>
                                        <i className="fas fa-calendar-day me-1"></i> TODAY'S ADS
                                    </span>
                                    <div className="icon-circle-small">
                                        <i className="fas fa-ad text-warning"></i>
                                    </div>
                                </div>
                                <div className="stat-value" style={{ color: '#ffffff' }}>{todayCounts.ads}</div>
                                <Link to="/admin/ads-list" style={linkStyle}>View today →</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;